import { useI18n } from '@affine/i18n';
import * as Dialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { useEffect } from 'react';
import { useTransition } from 'react-transition-state';

import * as styles from './modal.css';

// a QuickSearch modal that can be used to display a QuickSearch command
// it has a smooth animation and can be closed by clicking outside of the modal

export interface QuickSearchModalProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
}

const animationTimeout = 120;

export const QuickSearchModal = ({
  onOpenChange,
  open,
  children,
}: React.PropsWithChildren<QuickSearchModalProps>) => {
  const t = useI18n();
  const [{ status }, toggle] = useTransition({
    timeout: animationTimeout,
  });
  useEffect(() => {
    toggle(open);
  }, [open]);
  return (
    <Dialog.Root modal open={status !== 'exited'} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.modalOverlay} />
        <div className={styles.modalContentWrapper}>
          <Dialog.Content
            style={assignInlineVars({
              [styles.animationTimeout]: `${animationTimeout}ms`,
            })}
            className={styles.modalContent}
            data-state={status}
            // Radix a11y: explicitly "no description" — a search palette has nothing to describe;
            // this silences the Missing-Description warning without fake filler text.
            aria-describedby={undefined}
          >
            {/* Radix a11y: a Dialog needs a Title for screen readers; this one is visual-noise-free,
                so announce it invisibly (the pattern the shared Modal uses for its no-title case). */}
            <VisuallyHidden.Root asChild>
              <Dialog.Title>{t['Quick Search']()}</Dialog.Title>
            </VisuallyHidden.Root>
            {children}
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
